import { useState } from 'react'
import { Globe, Trash2, ExternalLink, Crown } from 'lucide-react'

import { cn } from '@/common/lib/utils'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import useMutationDetachDomain from '../../api/hooks/useMutationDetachDomain'

type DomainCardProps = {
	domain: string
	isMain: boolean
}

export default function DomainCard({ domain, isMain }: DomainCardProps) {
	const [showDeleteDialog, setShowDeleteDialog] = useState(false)
	const { mutate: detachDomain, isPending } = useMutationDetachDomain()

	const handleDelete = () => {
		detachDomain(
			{ domain },
			{
				onSuccess: () => {
					setShowDeleteDialog(false)
				},
			},
		)
	}

	const handleVisit = () => {
		window.open(`https://${domain}`, '_blank')
	}

	return (
		<>
			<Card
				className={cn(
					isMain &&
						'bg-gradient-to-br from-amber-50/50 to-orange-50/30 ring-2 ring-amber-500 dark:from-amber-950/20 dark:to-orange-950/10',
				)}
			>
				<CardHeader>
					<div className="flex items-start justify-between">
						<div className="flex items-center gap-2">
							{isMain ? (
								<Crown className="h-5 w-5 text-amber-500" />
							) : (
								<Globe className="h-5 w-5 text-muted-foreground" />
							)}
							<CardTitle className="break-all text-lg">{domain}</CardTitle>
						</div>
						<div className="flex flex-shrink-0 items-center gap-2">
							{isMain && (
								<Badge className="bg-amber-500 text-white hover:bg-amber-600">
									โดเมนหลัก
								</Badge>
							)}
							<Button
								variant="ghost"
								size="icon"
								onClick={handleVisit}
								title="เปิดโดเมน"
							>
								<ExternalLink className="h-4 w-4" />
							</Button>
							{!isMain && (
								<Button
									variant="ghost"
									size="icon"
									onClick={() => setShowDeleteDialog(true)}
									disabled={isPending}
									title="ลบโดเมน"
								>
									<Trash2 className="h-4 w-4 text-destructive" />
								</Button>
							)}
						</div>
					</div>
					<CardDescription>
						{isMain ? 'โดเมนหลักของระบบ' : 'โดเมนเสริม'}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="text-sm text-muted-foreground">
						<p>
							URL: <span className="text-primary">https://{domain}</span>
						</p>
					</div>
				</CardContent>
			</Card>

			<AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>ยืนยันการลบโดเมน</AlertDialogTitle>
						<AlertDialogDescription>
							คุณแน่ใจหรือไม่ว่าต้องการลบโดเมน{' '}
							<span className="break-all font-semibold">{domain}</span>{' '}
							การดำเนินการนี้ไม่สามารถย้อนกลับได้
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isPending}>ยกเลิก</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDelete}
							disabled={isPending}
							className="bg-destructive hover:bg-destructive/90"
						>
							{isPending ? 'กำลังลบ...' : 'ลบโดเมน'}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
